/**
 * A caixa de escrita.
 *
 * Três coisas que quase todos os clientes fazem mal e aqui estão resolvidas:
 *   1. o RASCUNHO sobrevive a trocar de canal (guardado fora do React, num Map
 *      de módulo — trocar de canal desmonta o componente, não a memória);
 *   2. os ANEXOS sobem enquanto se escreve, não no envio: quando se carrega em
 *      enviar o ficheiro já lá está e a mensagem sai instantânea;
 *   3. o "está a escrever" é atirado no máximo de 3 em 3 segundos, não a cada
 *      tecla — senão são 600 pacotes por minuto por pessoa, por nada.
 */

import { Limits, type Message, Permission, type Snowflake } from "@greenvoice/shared";
import {
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { formatBytes } from "../lib/format";
import { UploadError, uploadAttachment } from "../lib/upload";
import { can, selfPermissions, typingText } from "../store/selectors";
import { useStore } from "../store/store";
import { EmojiPickerButton } from "./EmojiPicker";
import { IconClose, IconPlus, IconSend } from "./Icons";

/** Rascunhos por canal — fora do React, porque o componente é desmontado. */
const drafts = new Map<Snowflake, string>();

const TYPING_INTERVAL_MS = 3_000;
const COUNTER_FROM = Limits.messageContent.max - 200;
const MAX_ROWS_PX = 340;

interface Pending {
  key: string;
  file: File;
  progress: number;
  fileId: Snowflake | null;
  error: string | null;
  controller: AbortController;
}

interface MessageComposerProps {
  channelId: Snowflake;
  replyTo: Message | null;
  onCancelReply(): void;
}

export function MessageComposer({ channelId, replyTo, onCancelReply }: MessageComposerProps) {
  const state = useStore();
  const channel = state.channels[channelId];

  const [draft, setDraft] = useState(() => drafts.get(channelId) ?? "");
  const [pending, setPending] = useState<Pending[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [dragging, setDragging] = useState(false);

  const input = useRef<HTMLTextAreaElement>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  const dragDepth = useRef(0);

  // Espelho do estado para os closures que correm fora do render (cleanup de
  // efeito, callbacks de upload) verem sempre a lista actual.
  const pendingRef = useRef<Pending[]>([]);
  pendingRef.current = pending;

  const permissions = selfPermissions(state, channel?.serverId ?? null, channel);
  const canSend = can(permissions, Permission.SEND_MESSAGES);
  const canAttach = can(permissions, Permission.ATTACH_FILES);
  const bypassSlowmode = can(permissions, Permission.MANAGE_MESSAGES);
  const slowmode = bypassSlowmode ? 0 : (channel?.slowmodeSeconds ?? 0);

  // Trocar de canal: buscar o rascunho guardado e devolver o foco à caixa.
  useEffect(() => {
    setDraft(drafts.get(channelId) ?? "");
    setPending([]);
    setError(null);
    setCooldown(0);
    input.current?.focus();
  }, [channelId]);

  // Responder põe o cursor na caixa — quem carrega em "responder" quer escrever.
  useEffect(() => {
    if (replyTo) input.current?.focus();
  }, [replyTo]);

  useLayoutEffect(() => {
    const element = input.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_ROWS_PX)}px`;
  }, [draft]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((v) => (v <= 1 ? 0 : v - 1)), 1_000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  // Sair do canal com uploads a meio: cancela-os, senão continuam a gastar
  // banda por um envio que já ninguém vai fazer.
  useEffect(() => {
    return () => {
      for (const item of pendingRef.current) {
        if (!item.fileId) item.controller.abort();
      }
    };
  }, [channelId]);

  function onChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value.slice(0, Limits.messageContent.max);
    setDraft(value);
    if (value) drafts.set(channelId, value);
    else drafts.delete(channelId);

    const now = Date.now();
    if (value && now - lastTyping.current > TYPING_INTERVAL_MS) {
      lastTyping.current = now;
      state.notifyTyping(channelId);
    }
  }

  function attach(files: FileList | File[]) {
    const incoming = Array.from(files);
    if (!incoming.length) return;

    const room = Limits.attachmentsPerMessage - pendingRef.current.length;
    if (room <= 0) {
      setError(`Máximo de ${Limits.attachmentsPerMessage} arquivos por mensagem.`);
      return;
    }
    if (incoming.length > room) {
      setError(`Só cabem mais ${room} arquivo(s) nesta mensagem.`);
    }

    for (const [index, file] of incoming.slice(0, room).entries()) {
      const item: Pending = {
        key: `${file.name}:${file.size}:${Date.now()}:${index}`,
        file,
        progress: 0,
        fileId: null,
        error: null,
        controller: new AbortController(),
      };
      setPending((list) => [...list, item]);

      void uploadAttachment(
        file,
        (fraction) => setPending((list) => list.map((p) => (p.key === item.key ? { ...p, progress: fraction } : p))),
        item.controller.signal,
      )
        .then(({ fileId }) =>
          setPending((list) => list.map((p) => (p.key === item.key ? { ...p, fileId, progress: 1 } : p))),
        )
        .catch((caught: unknown) => {
          const aborted = caught instanceof UploadError && caught.aborted;
          if (aborted) return; // o utilizador cancelou; a linha já saiu da lista
          setPending((list) => list.map((p) => (p.key === item.key ? { ...p, error: (caught as Error).message } : p)));
        });
    }
  }

  /**
   * Emoji escolhido no balão. Entra ONDE O CURSOR ESTÁ, não coladdo ao fim: a
   * pessoa que está a corrigir o meio de uma frase não quer o emoji lá atrás.
   * O cursor fica logo a seguir ao que entrou, e o foco volta à caixa — sem
   * isso, escolher dois emojis seguidos obrigava a clicar na caixa entre eles.
   */
  function insertEmoji(token: string) {
    const element = input.current;
    const start = element?.selectionStart ?? draft.length;
    const end = element?.selectionEnd ?? draft.length;
    // Um espaço a seguir para o token não colar à palavra que vier depois — é
    // o `<:festa:1>a` que o parser leria como texto e não como emoji.
    const piece = `${token} `;
    const value = (draft.slice(0, start) + piece + draft.slice(end)).slice(0, Limits.messageContent.max);

    setDraft(value);
    if (value) drafts.set(channelId, value);
    else drafts.delete(channelId);

    // Depois do render, senão o React reescreve o valor e leva o cursor ao fim.
    window.requestAnimationFrame(() => {
      const caret = Math.min(start + piece.length, value.length);
      element?.focus();
      element?.setSelectionRange(caret, caret);
    });
  }

  function drop(item: Pending) {
    if (!item.fileId) item.controller.abort();
    setPending((list) => list.filter((p) => p.key !== item.key));
  }

  const uploading = pending.some((p) => !p.fileId && !p.error);
  const attachmentIds = pending.flatMap((p) => (p.fileId ? [p.fileId] : []));
  const ready = (draft.trim().length > 0 || attachmentIds.length > 0) && !uploading;

  async function submit() {
    if (!ready || sending || cooldown > 0 || !canSend) return;
    setSending(true);
    setError(null);
    const content = draft;

    // Limpar ANTES do await: a caixa tem de esvaziar no instante do Enter, ou a
    // pessoa escreve por cima do que já vai a caminho.
    setDraft("");
    drafts.delete(channelId);
    setPending([]);
    onCancelReply();

    try {
      await state.sendMessage(channelId, content, {
        replyToId: replyTo?.id ?? null,
        attachmentIds,
      });
      if (slowmode > 0) setCooldown(slowmode);
    } catch (caught) {
      // Falhou: devolve-se o texto para não se perder o que se escreveu.
      setDraft(content);
      drafts.set(channelId, content);
      setError((caught as Error).message);
    } finally {
      setSending(false);
      input.current?.focus();
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Escape" && replyTo) {
      onCancelReply();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit();
    }
  }

  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(event.clipboardData.files);
    if (!files.length || !canAttach) return;
    event.preventDefault();
    attach(files);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (canAttach) attach(event.dataTransfer.files);
  }

  if (!canSend) {
    return (
      <div className="composer composer--locked">
        <p>Você não tem permissão para escrever neste canal.</p>
      </div>
    );
  }

  return (
    <div
      className={`composer${dragging ? " is-dragging" : ""}`}
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        if (canAttach) setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragging(false);
      }}
      onDrop={onDrop}
    >
      {replyTo ? (
        <div className="composer__reply">
          <span>
            Respondendo a <strong>{replyTo.author.displayName ?? replyTo.author.username}</strong>
          </span>
          <button type="button" className="icon-button" onClick={onCancelReply} title="Cancelar">
            <IconClose size={14} />
          </button>
        </div>
      ) : null}

      {pending.length ? (
        <div className="composer__attachments">
          {pending.map((item) => (
            <div key={item.key} className={`upload${item.error ? " has-error" : ""}`}>
              <div className="upload__meta">
                <strong title={item.file.name}>{item.file.name}</strong>
                <small>{item.error ?? formatBytes(item.file.size)}</small>
              </div>
              <div className="upload__bar">
                <span style={{ width: `${Math.round(item.progress * 100)}%` }} />
              </div>
              <button type="button" className="icon-button" onClick={() => drop(item)} title="Remover">
                <IconClose size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="composer__row">
        <input
          ref={filePicker}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) attach(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="icon-button composer__attach"
          disabled={!canAttach}
          title={canAttach ? "Anexar arquivo" : "Sem permissão para anexar"}
          onClick={() => filePicker.current?.click()}
        >
          <IconPlus size={20} />
        </button>

        <textarea
          ref={input}
          value={draft}
          rows={1}
          placeholder={cooldown > 0 ? `Aguarde ${cooldown}s (modo lento)` : `Escrever em #${channel?.name ?? ""}`}
          disabled={cooldown > 0}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />

        {draft.length >= COUNTER_FROM ? (
          <span className="composer__counter">{Limits.messageContent.max - draft.length}</span>
        ) : null}

        <EmojiPickerButton
          serverId={channel?.serverId ?? null}
          title="Emoji"
          onPick={(picked) => insertEmoji(picked.token)}
        />

        <button
          type="button"
          className="icon-button composer__send"
          disabled={!ready || sending || cooldown > 0}
          onClick={() => void submit()}
          title={uploading ? "Enviando arquivos…" : "Enviar"}
        >
          <IconSend size={20} />
        </button>
      </div>

      {error ? (
        <p className="composer__error" role="alert">
          {error}
        </p>
      ) : null}

      <TypingLine channelId={channelId} />
    </div>
  );
}

/**
 * "Fulano está a escrever…". O texto vem do selector; aqui só se garante que a
 * linha DESAPARECE: os avisos de escrita expiram por tempo, e sem este relógio
 * ficariam colados no ecrã até chegar outro evento qualquer.
 */
function TypingLine({ channelId }: { channelId: Snowflake }) {
  const state = useStore();
  const pruneTyping = state.pruneTyping;

  useEffect(() => {
    const timer = window.setInterval(() => pruneTyping(), 1_000);
    return () => window.clearInterval(timer);
  }, [pruneTyping]);

  const label = typingText(state, channelId);
  if (!label) return <div className="composer__typing" aria-hidden="true" />;

  return (
    <div className="composer__typing" role="status">
      <span className="typing-dots">
        <i />
        <i />
        <i />
      </span>
      {label}
    </div>
  );
}
